// @ts-check
import './plot-map-app.css';
import { h } from 'preact';
import { useState, useCallback, useEffect } from 'preact/hooks';

import useMultipleAdminAjax from '../../hooks/useMultipleAdminAjax';
import useFilters from '../../hooks/useFilters';
import useSvgMap from '../../hooks/useSvgMap';
import useIntersect from '../../hooks/useIntersect';
import useWindowSize from '../../hooks/useWindowSize';

import { getPlotNumberForModal, getPlotNumbersByGroup, getFilterMatchCount } from '../../utils/plotNumberHelpers';
import { getFiltersForModal, numberOfSetFilters, isMatch } from '../../utils/filterHelpers';
import { sortAndFilterDefaultStatuses, areThereDefaultHighLightedPlotsOnTheActiveView } from '../../utils/statusLegendHelpers';
import { getShapesForSvgMap, getPlotNumberIdFromShapeLink, getGroupIdFromShapeLink } from '../../utils/svgMapHelpers';
import { getGroupLevelFromView, getGroupById } from '../../utils/groupHelpers';

// components
import PlotMapAppWrapper from '../PlotMapAppWrapper';
import MapViewport from '../MapViewport';
import SvgMap from '../SvgMap';
import PlotDetailModal from '../PlotDetailModal/PlotDetailModal';
import HeaderNav from '../HeaderNav';
import GroupNav from '../GroupNav';
import ViewNavFloatingModal from '../ViewNavFloatingModal';
import StatusLegend from '../StatusLegend/StatusLegend';
import FilterButton from '../FilterButton/FilterButton';
import CustomShapeModal from '../CustomShapeModal';
import OnBoardingModal from '../OnBoardingModal';
import FilterModal from '../FilterModal/FilterModal';
import GroupDescription from '../GroupDescription';
import 'react-photoswipe-2/lib/photoswipe.css';
import { PhotoSwipe } from 'react-photoswipe-2';

const { __, sprintf } = wp.i18n;

// TODO: move utilfunctions?
const isInView = (entry) => entry.intersectionRatio > 1;
const isUnFocused = (entry) => entry.intersectionRatio < 0.5;
// TODO: height of 10rem (10x16) is also defined in css (not dry)
/**
 *
 * @param {{ width: number, height: number }} windowSize
 * @param {{ width: number, height: number }} viewSize
 * @param {boolean} isHeaderActive
 * @param {boolean} isFooterActive
 * @param {boolean} isDescriptionActive
 * @returns {{ width: number, height: number }}
 */
const getMapSize = (windowSize, viewSize, isHeaderActive, isFooterActive, isDescriptionActive) => {
	let additionalHeight = 0;
	additionalHeight += isHeaderActive ? 5*16 : 0;
	additionalHeight += isFooterActive ? 5*16 : 0;
	additionalHeight += isDescriptionActive ? 50 : 0;
	return {
		width: (windowSize.height-additionalHeight)/viewSize.height*viewSize.width,
		height: windowSize.height-additionalHeight
	};
};

/**
 * @param {boolean} isFiltersActive
 * @param {number} groupCount
 * @returns {boolean}
 */
const isHeaderActive = (isFiltersActive, groupCount) => isFiltersActive || groupCount > 1;

/**
 * @param {number} numberOfStatuses
 * @returns {boolean}
 */
const isFooterActive = (numberOfStatuses) => numberOfStatuses > 0;

// TODO: refactor into smaller pieces
/**
 * @param {object} props
 * @param {string} props.id
 * @param {string} props.placeholderBackgroundImageSrc
 * @param {boolean} props.displayLotNumbers
 * @param {string} props.fetchGroupsUrl
 * @param {string} props.fetchPlotNumbersUrl
 * @param {string=} props.fetchFiltersUrl
 * @param {object} props.filterOnBoarding
 * @param {import('./types').PlotProperty[]} props.informationToShowForAPlot
 * @param {object} props.registerButtonLink
 * @param {import('./types').PlotStatusStatus[]} props.statusLegendDefaults
 */
export default function PlotMapApp({
	id,
	placeholderBackgroundImageSrc,
	displayLotNumbers = false,
	fetchGroupsUrl,
	fetchPlotNumbersUrl,
	fetchFiltersUrl,
	filterOnBoarding,
	informationToShowForAPlot,
	registerButtonLink,
	statusLegendDefaults
}) {
	const [[fetchedPlotNumbers, fetchedGroups], { loading: loadingPlotNumbersAndGroups, error: errorPlotNumbersAndGroups }] = useMultipleAdminAjax({ fetchUrls: [fetchPlotNumbersUrl, fetchGroupsUrl] });
	const [
		filters, filterValues,
		{
			setFiltersWithPlotNumbers,
			filtersLoaded,
			isFiltersActive,
			handleFilterChange,
			handleFilterReset,
			handleFiltersReset,
			loading: loadingFilters,
			error: errorFilters
		}
	] = useFilters({ fetchUrl: fetchFiltersUrl });
	const [shapes, setShapes, {
		shapeSelected,
		handleShapeClick,
		handleShapeMouseEnter,
		handleShapeMouseLeave
	}] = useSvgMap();
	const [shapeChosen, setShapeChosen] = useState(null);

	const [statuses, setStatuses] = useState([]);

	const [plotNumbers, setPlotNumbers] = useState([]);
	const [activeGroup,	setActiveGroup] = useState(null);
	const [activeView, setActiveView] = useState(null);
	const [lastActiveLvl1ViewId, setLastActiveLvl1ViewId] = useState(null);
	const [plotNumberForModal, setPlotNumberForModal] = useState(null);
	const [galleryImages, setGalleryImages] = useState([]);
	const [isPlotDetailModalOpen, setPlotDetailModalOpenStatus] = useState(false);
	const [isCustomShapeModalOpen, setCustomShapeModalOpen] = useState(false);
	const [isFullScreenImageGalleryOpen, setFullScreenImageGalleryOpenStatus] = useState(false);
	const [fullScreenImageGalleryIndex, setFullScreenImageGalleryIndex] = useState(0);
	const [isFilterModalOpen, setFilterModalOpenStatus] = useState(false);
	const [isFilterOnBoardingModalOpen, setFilterOnBoardingModalOpenStatus] = useState(true);
	const [setRefLoadedContainer, entry, refLoadedContainer] = useIntersect({ threshold: [1, 0.5] });
	const [mapViewportInitialScrollLeft, setMapViewportInitialScrollLeft] = useState(0);
	const [groupDescription, setGroupDescription] = useState(null);
	const windowSize = useWindowSize();

	useEffect(() => {
		if (isUnFocused(entry)) {
			setPlotDetailModalOpenStatus(false);
			setFilterModalOpenStatus(false);
			setCustomShapeModalOpen(false);
		}
	}, [entry]);

	useEffect(() => {
		if (Array.isArray(fetchedGroups) && fetchedGroups.length > 0) {
			// NOTE: show all statuses as selected when there is no single plot number with the "available" status on the current active view (for ux purposes)
			// TODO: this assumes the PlotAppMap component knows the content of the statusLegendDefaults prop
			const showAllStatuses = isFiltersActive || !areThereDefaultHighLightedPlotsOnTheActiveView(fetchedPlotNumbers, fetchedGroups[0]?.views[0], statusLegendDefaults);
			const statusesSortedAndFiltered = sortAndFilterDefaultStatuses(fetchedPlotNumbers.map(plotNumber => ({
				...plotNumber.status,
				isSelected: showAllStatuses || statusLegendDefaults.filter(status => status.isSelected).map(status => status.value).indexOf(plotNumber.status.value) >= 0
			})), statusLegendDefaults);

			if (isFiltersActive) {
				statusesSortedAndFiltered.push({ label: __('No filter match', 'real-estate-project'), value: 'no-filter-match' });
			}

			setStatuses(statusesSortedAndFiltered);
		}
	}, [fetchedGroups]);


	useEffect(() => {
		if (activeGroup) {
			return;
		}

		if (Array.isArray(fetchedGroups) && fetchedGroups.length > 0 && plotNumbers.length > 0) {
			const foundGroups = fetchedGroups.filter(group => group.level === 0);

			if (foundGroups.length !== 1) {
				return;
			}

			setActiveGroup({
				...foundGroups[0]
			});
		}
	}, [fetchedGroups, plotNumbers]);

	useEffect(() => {
		if (!activeGroup) {
			return;
		}

		if (activeGroup.views.length <= 0) {
			return;
		}

		// Update the activeView when it is in de current group, set last active level 1 view, otherwise default to first view in current group
		setActiveView(prevActiveView => activeGroup.views.find(view => view.id === lastActiveLvl1ViewId) || activeGroup.views.find(view => view.id === prevActiveView?.id) || activeGroup.views[0]);
	}, [activeGroup]);

	useEffect(() => {
		if (!activeGroup) {
			return;
		}

		setGroupDescription(activeGroup.description ? windowSize.width >= 700 ? activeGroup.description.long : activeGroup.description.short : null);
	}, [activeGroup, windowSize]);

	useEffect(() => {
		if (activeGroup?.level === 0) {
			setLastActiveLvl1ViewId(activeView.id);
		}

	}, [activeView]);

	useEffect(() => {
		if (!activeView) {
			return;
		}

		setShapes(getShapesForSvgMap(
			activeView.shapes,
			plotNumbers,
			fetchedGroups,
			shapeSelected ? shapeSelected : null,
			statuses,
			isFiltersActive
		));
	}, [activeView, plotNumbers, fetchedGroups, shapeSelected, statuses]);

	useEffect(() => {
		if (Array.isArray(fetchedPlotNumbers)) {
			setPlotNumbers(fetchedPlotNumbers);
		}
	}, [fetchedPlotNumbers]);

	useEffect(() => {
		if (!fetchedPlotNumbers || !filtersLoaded) {
			return;
		}

		setFiltersWithPlotNumbers(fetchedPlotNumbers);
	}, [fetchedPlotNumbers, filtersLoaded]);

	useEffect(() => {
		if (!Array.isArray(fetchedPlotNumbers) || filters.length === 0) {
			return;
		}
		setPlotNumbers(fetchedPlotNumbers.map(plotNumber => ({
			...plotNumber,
			isMatch: filterValues.filter(filter => isMatch(filter.name, plotNumber, filter.value)).length === filterValues.length
		})));
	}, [filterValues, filters.length]);

	const handleStatusLegendChange = useCallback(value => {
		setStatuses(statuses.map(status => ({
			...status,
			isSelected: value === status.value ? !status.isSelected : status.isSelected
		})));
	  }, [statuses]);

	useEffect(() => {
		if (!shapeChosen) {
			return;
		}

		setCustomShapeModalOpen(false);

		const clickedPlotNumberId = getPlotNumberIdFromShapeLink(shapeChosen.link);
		const clickedGroupId = getGroupIdFromShapeLink(shapeChosen.link);

		if (clickedPlotNumberId) {
			const updatedPlotNumbers = plotNumbers.map(plotNumber => ({
				...plotNumber,
				isSelected: clickedPlotNumberId === plotNumber.id
			}));

			const selectedPlotNumbers = updatedPlotNumbers.filter(plotNumber => plotNumber.isSelected);
			setPlotNumberForModal(selectedPlotNumbers.length === 1 ? selectedPlotNumbers[0] : null);
			setFullScreenImageGalleryIndex(0);

			setPlotDetailModalOpenStatus(true);
		}

		if (clickedGroupId) {
			const foundGroups = fetchedGroups.filter(group => group.id === clickedGroupId);

			if (foundGroups.length === 1) {
				setActiveGroup({
					...foundGroups[0]
				});
			}
		}

		setFilterModalOpenStatus(false);
	}, [shapeChosen]);

	useEffect(() => {
		//TODO: bit hacky
		const plotNumberId = shapeSelected?.link ? getPlotNumberIdFromShapeLink(shapeSelected?.link) : null;
		// TODO: base on if hover is support for input device, now window size
		if (plotNumberId || windowSize && windowSize.width >= 500) {
			setShapeChosen(shapeSelected);
		}
	}, [shapeSelected]);

	useEffect(() => {
		if (!isInView(entry) && refLoadedContainer && refLoadedContainer.nodeType === 1) {
			refLoadedContainer.scrollIntoView();
		}

		setCustomShapeModalOpen(!!shapeSelected);
	}, [shapeSelected]);

	const onShapeChosen = useCallback(() => {
		setShapeChosen(shapeSelected);
	}, [shapeSelected]);

	const handleCloseCustomShapeModal = useCallback(() => {
		setCustomShapeModalOpen(false);
	}, []);

	const handleOutsidePlotClick = useCallback(() => {
		setPlotDetailModalOpenStatus(false);
	}, [plotNumbers]);

	const handleClosePlotNumberModal = useCallback(() => {
		setPlotDetailModalOpenStatus(false);
	}, []);

	const handleOpenFilterModal = useCallback(() => {
		setFilterOnBoardingModalOpenStatus(false);
		setFilterModalOpenStatus(true);
		setPlotDetailModalOpenStatus(false);
	}, []);

	const handleCloseFilterModal = useCallback(() => {
		setFilterModalOpenStatus(false);
	}, []);

	const handleCloseFilterOnboardingModal = useCallback(() => {
		setFilterOnBoardingModalOpenStatus(false);
	}, []);

	const getMatchCountForFilter = useCallback((name, value) => (
		fetchedPlotNumbers.filter(plotNumber => filterValues.filter(filter => isMatch(filter.name, plotNumber, filter.name === name ? value : filter.value)).length === filterValues.length).length
	), [fetchedPlotNumbers, filterValues]);

	const handleOpenFullScreenImageGallery = useCallback((openIndex, images) => {
		// TODO: fallback for when there are no width and height known for all images (Nieuwbouw Nederland source has this problem), remove when not needed anymore
		if (images.filter(image => image.width && image.height).length !== images.length) {
			images = images.map(image => {
				const imageElement = document.querySelector('.image-gallery-image-id-' + image.id + ' > img');
				return {
					...image,
					width: imageElement ? imageElement.naturalWidth : image.width,
					height: imageElement ? imageElement.naturalHeight : image.height
				};
			});
		}

		setGalleryImages(images);
		setFullScreenImageGalleryIndex(openIndex);
		setFullScreenImageGalleryOpenStatus(true);
	}, []);

	const handleCloseFullScreenImageGallery = useCallback(() => {
		setFullScreenImageGalleryOpenStatus(false);
	}, []);

	const onCloseViewport = useCallback(() => {
		const foundGroups = fetchedGroups.filter(group => group.level === 0);

		if (foundGroups.length !== 1) {
			return;
		}

		setActiveGroup({
			...foundGroups[0]
		});
	}, [fetchedGroups]);

	const handleSwitchView = useCallback((id) => {
		const foundViews = activeGroup.views.filter(view => view.id === id);

		setActiveView(foundViews.length === 1 ? foundViews[0] : null);
		setPlotDetailModalOpenStatus(false);
	}, [activeGroup]);

	const handleSvgBoundingBoxChange = useCallback((SvgRect) => {
		if (!activeView) {
			return;
		}

		const mapSize = getMapSize(windowSize, activeView.backgroundImage, isHeaderActive(isFiltersActive, fetchedGroups.length), isFooterActive(statuses.length), !!groupDescription);

		if (mapSize.width < windowSize.width) {
			return;
		}

		const mapSizeScaleFactor = mapSize.width / activeView.backgroundImage.width;
		const correctedBoundingBoxCenterX = (SvgRect.width / 2 + SvgRect.x) * mapSizeScaleFactor;

		setMapViewportInitialScrollLeft(correctedBoundingBoxCenterX - (windowSize.width / 2));
	}, [windowSize, activeView, groupDescription]);

	useEffect(() => {
		if (windowSize.width < 512 && (isPlotDetailModalOpen || isFilterModalOpen)) {
			document.body.classList.add('plot-map-no-scroll');
		}
		else {
			document.body.classList.remove('plot-map-no-scroll');
		}
	}, [isPlotDetailModalOpen, isFilterModalOpen, windowSize.width]);

	// TODO: FilterModal refactor into children?
	return (
		<PlotMapAppWrapper
			placeholderBackgroundImageSrc={placeholderBackgroundImageSrc}
			loading={loadingPlotNumbersAndGroups || loadingFilters}
			error={errorPlotNumbersAndGroups || errorFilters}
			noResults={fetchedPlotNumbers?.length === 0}
		>
			{fetchedPlotNumbers?.length > 0 && <div
				ref={setRefLoadedContainer}
				class={`plot-map-app-container${isInView(entry) ? ' is-in-view' : ''}${isHeaderActive(isFiltersActive, fetchedGroups.length) ? ' contains-header' : ''}${isFooterActive(statuses.length) ? ' contains-footer' : ''}${!!groupDescription ? ' contains-description' : ''}`}
			>
				{isHeaderActive(isFiltersActive, fetchedGroups.length) &&
					<HeaderNav>
						{isFiltersActive && filters.length > 0 &&
							<FilterButton
								onClickHandler={handleOpenFilterModal}
								numberOfFiltersActive={numberOfSetFilters(filterValues)}
							/>}
						{isFiltersActive && filterOnBoarding && <OnBoardingModal isOpen={isFilterOnBoardingModalOpen} onClose={handleCloseFilterOnboardingModal}>
							<h4>{filterOnBoarding.title}</h4>
							<p>{filterOnBoarding.text}</p>
							<p><button onClick={handleOpenFilterModal} type="button" className="button">
								{getMatchCountForFilter() === 1 ?
									sprintf(__('Filter %d result', 'real-estate-project'), getMatchCountForFilter()) :
									sprintf(__('Filter %d results', 'real-estate-project'), getMatchCountForFilter())}
							</button></p>
						</OnBoardingModal>}
						{activeGroup && activeView && fetchedGroups.length > 1 && <GroupNav
							activeGroup={activeGroup}
							onBack={getGroupLevelFromView(activeView, fetchedGroups) === 1 ? onCloseViewport : undefined}
							isCompact={windowSize?.width < 600}
						/>}
					</HeaderNav>}
				{groupDescription && <GroupDescription>{groupDescription}</GroupDescription>}
				{activeView &&
					<MapViewport
						windowSize={windowSize}
						mapSize={getMapSize(windowSize, activeView.backgroundImage, isHeaderActive(isFiltersActive, fetchedGroups.length), isFooterActive(statuses.length), !!groupDescription)}
						handleClick={handleOutsidePlotClick}
						initialScrollLeft={mapViewportInitialScrollLeft}
						floatingNav={activeGroup?.views.length > 1 && activeView &&
							<ViewNavFloatingModal
								views={activeGroup.views}
								activeView={activeView}
								onClick={handleSwitchView}
							/>}
					>
						<SvgMap
							shapes={shapes}
							plotNumbers={plotNumbers}
							groups={fetchedGroups}
							statuses={statuses}
							svgSize={activeView.backgroundImage}
							mapSize={getMapSize(windowSize, activeView.backgroundImage, isHeaderActive(isFiltersActive, fetchedGroups.length), isFooterActive(statuses.length), !!groupDescription)}
							backgroundImageSrc={activeView.backgroundImage.src || placeholderBackgroundImageSrc}
							handleShapeClick={handleShapeClick}
							handleShapeMouseEnter={handleShapeMouseEnter}
							handleShapeMouseLeave={handleShapeMouseLeave}
							onSvgBoundingBoxChange={handleSvgBoundingBoxChange}
							displayLotNumbers={displayLotNumbers}
						/>
					</MapViewport>}
				{activeView && windowSize.width < 500 && shapeSelected && getGroupIdFromShapeLink(shapeSelected?.link) &&
					<CustomShapeModal
						name={shapeSelected ? getGroupById(getGroupIdFromShapeLink(shapeSelected.link) || '', fetchedGroups)?.label : ''}
						plotNumbers={shapeSelected ? getPlotNumbersByGroup(
							getGroupById(
								getGroupIdFromShapeLink(shapeSelected?.link), fetchedGroups
							),
							plotNumbers
						): undefined}
						statuses={statuses}
						isActive={isCustomShapeModalOpen}
						onClick={onShapeChosen}
						onClose={handleCloseCustomShapeModal}
					/>
				}
				{isFooterActive(statuses.length) &&
					<StatusLegend
						statuses={statuses} mapId={id}
						handleChange={!isFiltersActive ? handleStatusLegendChange : undefined}
					/>}
				<PlotDetailModal
					{...getPlotNumberForModal(plotNumberForModal, informationToShowForAPlot)}
					registerLink={registerButtonLink}
					isActive={isPlotDetailModalOpen}
					onCloseHandler={handleClosePlotNumberModal}
					onFullScreenImageGalleryOpen={handleOpenFullScreenImageGallery}
				/>
				{filters.length > 0 &&
					<FilterModal
						mapId={id}
						filters={getFiltersForModal(filters, filterValues)}
						filterValues={filterValues}
						numberOfResults={getFilterMatchCount(plotNumbers)}
						numberOfFiltersActive={numberOfSetFilters(filterValues)}
						isActive={isFilterModalOpen}
						onFilterChangeHandler={handleFilterChange}
						onFilterResetHandler={handleFilterReset}
						onFiltersResetHandler={handleFiltersReset}
						onCloseHandler={handleCloseFilterModal}
						getMatchCountForFilter={getMatchCountForFilter}
						showNumberOfResultsInButton={fetchedGroups.length === 1}
					/>}
				<PhotoSwipe
					isOpen={isPlotDetailModalOpen && isFullScreenImageGalleryOpen}
					options={{
						shareEl: false,
						bgOpacity: 0.8,
						index: fullScreenImageGalleryIndex,
						history: false
					}}
					items={galleryImages.map(image => ({
						...image,
						thumbnail: image.src,
						w: image.width,
						h: image.height
					}))} onClose={handleCloseFullScreenImageGallery}
				/>
			</div>}
		</PlotMapAppWrapper>
	);
}
